import React from 'react';
import { Link } from 'react-router-dom';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ResourcesPage = () => {
  const sections = [
    {
      id: 'blog',
      title: 'Blog',
      icon: 'FileText',
      description: 'Articles, insights et actualités sur la formation professionnelle',
      link: '/blog-hub',
      color: 'from-[#96121D] to-[#96121D]/80',
      image: '/assets/images/business_presentation_team_four.png',
      alt: 'Professional business presentation showcasing CRF PERFECTION blog articles and industry insights',
      features: [
        'Articles d\'experts',
        'Tendances du marché',
        'Études de cas',
        'Guides pratiques'
      ]
    },
    {
      id: 'certifications',
      title: 'Certifications',
      icon: 'Award',
      description: 'Nos accréditations et certifications professionnelles',
      link: '/about/certifications',
      color: 'from-[#9B8E57] to-[#9B8E57]/80',
      image: '/assets/images/strategic_institutional_intervention_consultation.png',
      alt: 'Professional certification documents and accreditation badges representing CRF PERFECTION quality standards',
      features: [
        'Certifications ISO',
        'Accréditations officielles',
        'Labels de qualité',
        'Reconnaissances internationales'
      ]
    },
    {
      id: 'partenaires',
      title: 'Partenaires',
      icon: 'Handshake',
      description: 'Notre réseau de partenaires stratégiques et collaborateurs',
      link: '/about/partners',
      color: 'from-[#96121D] to-[#9B8E57]',
      image: '/assets/images/team_collaboration_meeting_two.png',
      alt: 'Strategic partnership meeting showcasing CRF PERFECTION collaboration network and business alliances',
      features: [
        'Partenaires académiques',
        'Entreprises partenaires',
        'Réseaux professionnels',
        'Collaborations internationales'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/assets/images/hero_carousel_professional_collaboration_one.png')] bg-cover bg-center"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#9B8E57]/20 px-6 py-3 rounded-full mb-8 border border-[#9B8E57]/30">
            <Icon name="Layers" size={20} className="text-[#9B8E57]" />
            <span className="text-[#9B8E57] font-semibold text-sm uppercase tracking-wider">Ressources & Réseau</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Blog, Certifications & Partenaires
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Explorez nos ressources, découvrez nos certifications et rencontrez nos partenaires stratégiques qui contribuent à notre excellence.
          </p>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-[#96121D]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-[#9B8E57]/10 rounded-full blur-3xl"></div>
      </section>

      {/* Main Content Sections */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections?.map((section, index) => (
              <div 
                key={section?.id}
                className={`group relative bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-gray-100 hover:border-[#9B8E57]/30 transition-all duration-500 hover:shadow-2xl ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } flex flex-col lg:flex`}
              >
                {/* Image Section */}
                <div className="lg:w-1/2 relative overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${section?.color} opacity-20 group-hover:opacity-30 transition-opacity duration-500`}></div>
                  <Image
                    src={section?.image}
                    alt={section?.alt}
                    className="w-full h-80 lg:h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-6 left-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${section?.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <Icon name={section?.icon} size={32} className="text-white" />
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className="lg:w-1/2 p-10 lg:p-12 flex flex-col justify-center">
                  <h2 className="text-4xl font-bold text-gray-900 mb-4 group-hover:text-[#96121D] transition-colors duration-300">
                    {section?.title}
                  </h2>
                  
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {section?.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3 mb-8">
                    {section?.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-3 text-gray-700">
                        <div className="w-6 h-6 bg-[#9B8E57]/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon name="Check" size={14} className="text-[#96121D]" />
                        </div>
                        <span className="font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={section?.link}
                    className={`inline-flex items-center space-x-3 bg-gradient-to-r ${section?.color} text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 w-fit group/btn`}
                  >
                    <span>Explorer</span>
                    <Icon name="ArrowRight" size={20} className="group-hover/btn:translate-x-2 transition-transform duration-300" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pourquoi Choisir CRF PERFECTION ?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Notre engagement envers l'excellence se reflète dans nos ressources et notre réseau
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100 hover:border-[#9B8E57]/30 transition-all duration-300 hover:shadow-xl">
              <div className="w-14 h-14 bg-gradient-to-br from-[#96121D] to-[#96121D]/80 rounded-xl flex items-center justify-center mb-6">
                <Icon name="TrendingUp" size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Contenu Actualisé</h3>
              <p className="text-gray-600">Articles et ressources régulièrement mis à jour</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100 hover:border-[#9B8E57]/30 transition-all duration-300 hover:shadow-xl">
              <div className="w-14 h-14 bg-gradient-to-br from-[#9B8E57] to-[#9B8E57]/80 rounded-xl flex items-center justify-center mb-6">
                <Icon name="Shield" size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Certifications Reconnues</h3>
              <p className="text-gray-600">Accréditations officielles et labels de qualité</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100 hover:border-[#9B8E57]/30 transition-all duration-300 hover:shadow-xl">
              <div className="w-14 h-14 bg-gradient-to-br from-[#96121D] to-[#9B8E57] rounded-xl flex items-center justify-center mb-6">
                <Icon name="Globe" size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Réseau International</h3>
              <p className="text-gray-600">Partenaires stratégiques à travers le monde</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100 hover:border-[#9B8E57]/30 transition-all duration-300 hover:shadow-xl">
              <div className="w-14 h-14 bg-gradient-to-br from-[#9B8E57] to-[#96121D] rounded-xl flex items-center justify-center mb-6">
                <Icon name="Users" size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Communauté Active</h3>
              <p className="text-gray-600">Échanges et collaboration entre professionnels</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#96121D] to-[#96121D]/90">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Rejoignez Notre Réseau d'Excellence
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Accédez à nos ressources exclusives et bénéficiez de notre réseau de partenaires.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/blog-hub"
              className="inline-flex items-center justify-center space-x-3 bg-white text-[#96121D] px-10 py-5 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
            >
              <Icon name="FileText" size={22} />
              <span>Lire le Blog</span>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center space-x-3 bg-[#9B8E57] text-white px-10 py-5 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
            >
              <Icon name="Mail" size={22} />
              <span>Nous Contacter</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesPage;